import { useEffect } from "react";

import { analytics } from "MVVM/Analytics";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useTrialEligibility } from "MVVM/Hooks/useTrialEligibility";
import { ClipQuota } from "MVVM/Utilities/ClipLimits";
import { TTierKey } from "MVVM/Views/Pricing/Components/PricingCards/types";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";

interface IClipEligibilityModalProps {
  clipQuota?: ClipQuota;
  userGroup?: string;
  isCs2Clip?: boolean;
  onClose: () => void;
  closeQuickEditModal?: () => void;
}

const TIER_CLIPS_PER_MONTH: Record<string, string> = {
  ESSENTIALS: "10",
  PRO: "50",
  CREATOR_PLAT: "Unlimited",
};

const getTierCopy = (userGroup?: string) => {
  switch (userGroup) {
    case "ESSENTIALS":
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: "PRO" as TTierKey,
      };
    case "PRO":
    case "PRO_PLUS":
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: "CREATOR_PLAT" as TTierKey,
      };
    case "CREATOR_PLAT":
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: null,
      };
    case "FREE":
    default:
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: "ESSENTIALS" as TTierKey,
      };
  }
};

export const ClipEligibilityModal = ({
  clipQuota,
  userGroup,
  isCs2Clip,
  onClose,
  closeQuickEditModal,
}: IClipEligibilityModalProps) => {
  const { isTrialEligible } = useTrialEligibility();
  const isInternalAccount = useIsInternalAccount();
  const tierCopy = getTierCopy(userGroup);
  const nextAvailableAt = clipQuota?.nextAvailableAt;

  useEffect(() => {
    analytics.track("Clip Eligibility Modal Viewed", {
      currentTier: userGroup,
      upgradeTier: tierCopy.upgradeTierKey,
      isTrialEligible,
      hasQuotaTimer: !!nextAvailableAt,
    });
  }, [userGroup, tierCopy.upgradeTierKey, isTrialEligible, nextAvailableAt]);

  const handleClose = () => {
    onClose();
    closeQuickEditModal?.();
  };

  const upgradeTier = tierCopy.upgradeTierKey || "ESSENTIALS";
  const clipsLabel = TIER_CLIPS_PER_MONTH[upgradeTier] || "More";
  const featureName =
    clipsLabel === "Unlimited"
      ? "Create Unlimited Clips"
      : `Create ${clipsLabel} Clips per Month`;

  return (
    <UnifiedUpgradeModal
      featureName={featureName}
      upgradeAction="Create More Clips"
      requiredTierKey={upgradeTier}
      analyticsSource="Clip Eligibility"
      showDiscordBotCta
      showMonthlyRewardsCta={isInternalAccount}
      isCs2Clip={isCs2Clip}
      onClose={handleClose}
    />
  );
};
